// 照护者认证服务 - 处理注册、登录和会话管理
import { 
  CarerRegisterRequest, 
  CarerLoginRequest, 
  AuthResponse,
  CarerAccount 
} from '../types/index';
import { apiConfigManager } from '../config/apiConfig';

class AuthService {
  private token: string | null = null;
  private currentCarer: CarerAccount | null = null;

  constructor() {
    this.loadStoredAuth();
    
    // 监听API后端切换事件
    window.addEventListener('apiBackendChanged', (event: any) => {
      console.log('🔄 认证服务检测到API后端切换:', event.detail);
      // 后端切换后清除认证状态，避免token冲突
      this.clearAuth();
    });
  }

  // 从localStorage加载已保存的认证信息
  private loadStoredAuth() {
    try {
      const storedToken = localStorage.getItem('icope_auth_token');
      const storedCarer = localStorage.getItem('icope_carer_data');
      
      if (storedToken && storedCarer) {
        this.token = storedToken;
        this.currentCarer = JSON.parse(storedCarer);
        console.log('🔐 已加载保存的认证信息:', this.currentCarer?.name);
      }
    } catch (error) {
      console.warn('⚠️ 加载认证信息失败:', error);
      this.clearAuth();
    }
  }

  // 保存认证信息到localStorage
  private saveAuth(token: string, carer: CarerAccount) {
    try {
      localStorage.setItem('icope_auth_token', token);
      localStorage.setItem('icope_carer_data', JSON.stringify(carer));
      this.token = token;
      this.currentCarer = carer;
      console.log('💾 认证信息已保存:', carer.name);

      // 🧹 登录成功后自动清理临时用户数据
      this.clearTempUserDataOnLogin();
    } catch (error) {
      console.error('❌ 保存认证信息失败:', error);
    }
  }

  // 清除认证信息
  private clearAuth() {
    localStorage.removeItem('icope_auth_token');
    localStorage.removeItem('icope_carer_data');
    this.token = null;
    this.currentCarer = null;
  }

  // HTTP请求封装（带认证）
  private async request<T = any>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const url = apiConfigManager.buildApiUrl(endpoint);
    
    const headers: any = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // 添加认证头
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    try {
      console.log(`🌐 认证API请求 [FastAPI]: ${options.method || 'GET'} ${url}`);
      
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // 首先检查响应状态
      if (!response.ok) {
        // 如果是401错误，清除本地认证信息
        if (response.status === 401) {
          this.clearAuth();
        }
        
        // 尝试解析错误响应的JSON，如果失败则使用默认错误信息
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          }
        } catch (parseError) {
          console.warn('⚠️ 无法解析错误响应JSON:', parseError);
        }
        
        throw new Error(errorMessage);
      }

      // 检查响应是否包含JSON内容
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('API响应不是有效的JSON格式');
      }

      const data = await response.json();
      
      console.log(`🔍 [AuthService] API原始响应:`, {
        url,
        method: options.method || 'GET',
        status: response.status,
        success: data.success,
        message: data.message,
        dataKeys: data.data ? Object.keys(data.data) : 'null',
        fullResponse: data,
        responseData: data.data
      });
      
      if (!data.success) {
        throw new Error(data.message || 'API请求失败');
      }
      
      console.log(`✅ 认证API响应成功: ${data.message}`);
      return data.data;
    } catch (error) {
      console.error(`❌ 认证API请求失败 ${url}:`, error);
      throw error;
    }
  }

  // 检查用户是否已存在
  async checkUserExists(phoneNumber: string): Promise<{
    exists: boolean;
    userInfo?: {
      name: string;
      role: string;
      lastLoginAt?: string;
    } | null;
  }> {
    const result = await this.request<{
      phoneNumber: string;
      exists: boolean;
      userInfo?: {
        name: string;
        role: string;
        lastLoginAt?: string;
      } | null;
    }>('/auth/check-user-exists', {
      method: 'POST',
      body: JSON.stringify({ phoneNumber }),
    });

    return {
      exists: result.exists,
      userInfo: result.userInfo
    };
  }

  // 发送短信验证码
  async sendSmsCode(phoneNumber: string, purpose: 'register' | 'login' = 'register'): Promise<void> {
    await this.request('/auth/send-sms', {
      method: 'POST',
      body: JSON.stringify({ phoneNumber, purpose }),
    });
  }

  // 验证短信验证码
  async verifySmsCode(phoneNumber: string, verificationCode: string): Promise<void> {
    await this.request('/auth/verify-sms', {
      method: 'POST',
      body: JSON.stringify({ phoneNumber, code: verificationCode }),
    });
  }

  // 照护者注册
  async register(request: CarerRegisterRequest): Promise<AuthResponse> {
    const result = await this.request<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (result.token && result.carer) {
      this.saveAuth(result.token, result.carer);
    }

    return result;
  }

  // 照护者登录
  async login(request: CarerLoginRequest): Promise<AuthResponse> {
    const result = await this.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (result.token && result.carer) {
      this.saveAuth(result.token, result.carer);
    }

    return result;
  }

  // 照护者登出
  async logout(): Promise<void> {
    try {
      if (this.token) {
        await this.request('/auth/logout', {
          method: 'DELETE',
        });
      }
    } catch (error) {
      console.warn('⚠️ 服务器登出失败:', error);
    } finally {
      this.clearAuth();
      console.log('✅ 照护者已登出');
    }
  }

  // 公共方法：清除认证信息（用于token过期等情况）
  forceLogout(): void {
    this.clearAuth();
    console.log('🔐 认证状态已清除，用户需要重新登录');
  }

  // 获取当前照护者信息
  async getCurrentCarer(clearOnError: boolean = false): Promise<CarerAccount | null> {
    if (!this.token) {
      return null;
    }

    try {
      const response = await this.request<{carer: CarerAccount}>('/auth/profile');
      const carer = response.carer;
      this.currentCarer = carer;
      
      // 更新localStorage中的信息
      if (carer) {
        localStorage.setItem('icope_carer_data', JSON.stringify(carer));
      }
      
      return carer;
    } catch (error) {
      console.error('❌ 获取当前照护者信息失败:', error);
      
      // 只有在明确要求时才清除认证信息（比如主动登出）
      if (clearOnError) {
        this.clearAuth();
      } else {
        console.log('🔄 保持本地认证状态，网络请求失败不影响用户体验');
      }
      
      return null;
    }
  }

  // 获取当前照护者信息（同步版本，避免不必要的API调用）
  getCurrentCarerSync(): CarerAccount | null {
    return this.currentCarer;
  }

  // 检查是否已登录
  isAuthenticated(): boolean {
    return !!(this.token && this.currentCarer);
  }

  // 获取当前认证状态
  getAuthState() {
    return {
      isAuthenticated: this.isAuthenticated(),
      token: this.token,
      carer: this.currentCarer
    };
  }

  // 检查照护者是否有权限管理指定用户
  canManageUser(userId: string): boolean {
    if (!this.currentCarer) {
      return false;
    }

    // 所有照护者只能管理自己拥有的用户档案
    return this.currentCarer.ownedProfiles.includes(userId);
  }

  // 添加用户档案到照护者
  addUserProfile(userId: string): void {
    if (this.currentCarer) {
      if (!this.currentCarer.ownedProfiles.includes(userId)) {
        this.currentCarer.ownedProfiles.push(userId);
        localStorage.setItem('icope_carer_data', JSON.stringify(this.currentCarer));
        console.log('✅ 用户档案已添加到照护者:', userId);
      }
    }
  }

  // 移除用户档案（软删除）
  async removeUserProfile(userId: string): Promise<void> {
    if (!this.isAuthenticated()) {
      throw new Error('未登录');
    }

    if (!this.canManageUser(userId)) {
      throw new Error('您没有权限移除此档案');
    }

    console.log(`🔍 软删除用户: ${userId}`);

    // 直接调用后端API删除（软删除）
    console.log('🌐 API模式，调用后端软删除:', userId);
    try {
      const response = await this.request(`users/${userId}`, {
        method: 'DELETE',
      });

      console.log('✅ API用户软删除成功:', userId);
      console.log('📊 软删除响应:', response);

      // 软删除成功后，立即更新本地缓存
      if (this.currentCarer && this.currentCarer.ownedProfiles) {
        this.currentCarer.ownedProfiles = this.currentCarer.ownedProfiles.filter(
          profileId => profileId !== userId
        );

        // 更新localStorage中的照护者信息
        localStorage.setItem('icope_carer_data', JSON.stringify(this.currentCarer));
        console.log('✅ 本地用户列表已更新，移除软删除的用户:', userId);
      }

    } catch (error) {
      console.error('❌ API软删除失败:', error);

      // 检查是否是权限错误
      if (error instanceof Error && error.message.includes('权限')) {
        throw new Error('您没有权限删除此档案');
      }

      // 检查是否是认证错误
      if (error instanceof Error && error.message.includes('unauthorized')) {
        throw new Error('认证失败，请重新登录后再试');
      }

      throw new Error('删除用户失败: ' + (error instanceof Error ? error.message : '未知错误'));
    }

    console.log('✅ 用户档案软删除成功:', userId);
  }

  // 获取当前token
  getToken(): string | null {
    return this.token;
  }

  // 获取认证头
  getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    // 🔍 智能token获取 - 先使用内存中的token，如果没有则从localStorage获取
    let token = this.token;
    if (!token) {
      token = localStorage.getItem('icope_auth_token');
      if (token) {
        console.log('🔄 [AuthService.getAuthHeaders] 从localStorage恢复token');
        // 恢复到内存中以便后续使用
        this.token = token;

        // 同时尝试恢复carer信息
        if (!this.currentCarer) {
          const storedCarer = localStorage.getItem('icope_carer_data');
          if (storedCarer) {
            try {
              this.currentCarer = JSON.parse(storedCarer);
              console.log('🔄 [AuthService.getAuthHeaders] 从localStorage恢复carer信息:', this.currentCarer?.name);
            } catch (e) {
              console.warn('⚠️ [AuthService.getAuthHeaders] 解析carer数据失败:', e);
            }
          }
        }
      }
    }

    // 🔍 调试日志 - 检查token存在性
    console.log('🔐 [AuthService.getAuthHeaders] 检查认证状态:', {
      memoryTokenExists: !!this.token,
      finalTokenExists: !!token,
      tokenLength: token ? token.length : 0,
      carerExists: !!this.currentCarer,
      carerName: this.currentCarer?.name || 'null',
      isAuthenticated: this.isAuthenticated()
    });

    if (token) {
      headers.Authorization = `Bearer ${token}`;
      console.log('✅ [AuthService.getAuthHeaders] 已添加认证头');
    } else {
      console.warn('⚠️ [AuthService.getAuthHeaders] Token不存在，请求将无认证');
    }

    return headers;
  }

  // 🧹 登录成功后清理临时用户数据
  private async clearTempUserDataOnLogin(): Promise<void> {
    try {
      console.log('🧹 [AuthService] 登录成功，开始清理临时用户数据...');

      // 动态导入LocalUserService避免循环依赖
      const { localUserService } = await import('./localUserService');
      await localUserService.clearAllTempUsers();

      // 清理快速用户数据
      const quickUserIds = JSON.parse(localStorage.getItem('quickUserIndex') || '[]');
      quickUserIds.forEach((userId: string) => {
        localStorage.removeItem(`quickUserData_${userId}`);
        sessionStorage.removeItem(`quickUserData_${userId}`);
      });
      localStorage.removeItem('quickUserIndex');
      sessionStorage.removeItem('quickUserData');

      // 🚨 注意：不要清理sessionStorage中的currentUserId，因为这会影响用户的正常使用流程
      // 用户登录后应该保持之前选择的用户档案状态
      // sessionStorage.removeItem('currentUserId'); // 注释掉这行，避免破坏用户体验

      console.log('✅ [AuthService] 临时用户数据清理完成');
    } catch (error) {
      console.error('❌ [AuthService] 清理临时用户数据失败:', error);
    }
  }

  // 刷新token（如果需要）
  async refreshToken(): Promise<boolean> {
    try {
      if (!this.token) {
        return false;
      }

      // 尝试获取当前用户信息来验证token是否仍然有效
      // 在token刷新场景下，不自动清除认证状态
      const carer = await this.getCurrentCarer(false);
      return !!carer;
    } catch (error) {
      console.warn('⚠️ Token刷新失败:', error);
      // 这里也不清除认证状态，让用户继续使用
      return false;
    }
  }
}

// 导出单例实例
export const authService = new AuthService();
export default AuthService;